import { useSelector, useDispatch } from "react-redux";
import { FieldArray, FormikProvider, useFormik } from "formik";
import { Card, CardBody, FormFeedback, Input, UncontrolledTooltip } from "reactstrap";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { formatModifyDate, getCurrencySymbol } from "../../../components/Common/CommonLogic";
import { cube_filled, oocl_logo, ship_filled, zim_logo } from "../../../assets/images";
import AsyncSelect from "react-select/async";
import debounce from 'lodash.debounce';
import Select from "react-select";
import { getActiveNetworkInquiriesViewDetails } from "../../../store/Sales/actions";
import { GET_COMMODITY_DATA } from "../../../store/Global/actiontype";
import { RESET_SHIPMENT_FORM_DATA } from "../../../store/InstantRate/actionType";
import { getAllIncoTerms } from "../../../store/InstantRate/actions";

const ShipmentInformationForm = ({ bookingFormik, bookingType, proceedToNext, bookingFrom, inquiryId }) => {
    const { searchForm, instantRateLocation, $instantActiveTab, incoterm, airLocation } = useSelector((state) => state?.instantRate);
    const { inquiry_data_loader, activeQuotationsViewDetails } = useSelector((state) => state?.sales);
    const quoteData = useSelector((state) => state.instantRate.quote_selected_data);
    const { cargoType_data, container_data, UOM_weight_data, currency_data, commodity_data } = useSelector((state) => state?.globalReducer);
    const [filteredCount, setFilteredCount] = useState(0);
    const [commodityDataStore, setCommodityDataStore] = useState('');
    const [originAddress, setOriginAddress] = useState('');
    const [destinationAddress, setDestinationAddress] = useState('');
    const dispatch = useDispatch();
    
    // Function to clear cache/form data
    const clearFormCache = () => {
        // Reset local component state
        setFilteredCount(0);
        setCommodityDataStore('');
        setOriginAddress('');
        setDestinationAddress('');
        
        // Reset Formik form data for shipment information
        bookingFormik.setFieldValue('cargo_value', '');
        bookingFormik.setFieldValue('commodity', null);
        bookingFormik.setFieldValue('incoterms', null);
        bookingFormik.setFieldValue('shipment_details', []);
        
        // Dispatch action to reset related Redux state
        // This will clear quote_selected_data and quote_selected_tenantdata
        // which prevents displaying previous values in fare breakdown
        dispatch({ type: RESET_SHIPMENT_FORM_DATA });
        
        console.log('Shipment form cache cleared');
    };

    useEffect(() => {
        //console.log(currency_data, "currency_data");
        dispatch({ type: GET_COMMODITY_DATA });
        dispatch(getAllIncoTerms());
    }, [])

    useEffect(() => {
        //console.log(quoteData, "quoteData in BookingForm");
        if (bookingFrom === 'viewBids') {
            //console.log(inquiryId, "inquiryId");
            if(bookingType === 'air') {
                console.log(quoteData, "quoteData in ShipmentInformationForm");
                setOriginAddress(airLocation.find(location => Number(location?.id) === Number(quoteData[0]?.originId)));
                setDestinationAddress(airLocation.find(location => Number(location?.id) === Number(quoteData[0]?.destinationId)));
            }
            if(bookingType === 'ocean_fcl') {
                setOriginAddress(instantRateLocation.find(location => Number(location?.id) === Number(quoteData[0]?.originId)));
                setDestinationAddress(instantRateLocation.find(location => Number(location?.id) === Number(quoteData[0]?.destinationId)));
            }
            

            if (inquiryId !== '' && inquiryId !== undefined) {
                let url = '?inquiryId=' + inquiryId;
                dispatch(getActiveNetworkInquiriesViewDetails(url));
            }
        }
    }, [quoteData, instantRateLocation, bookingFrom, inquiryId, airLocation])

    useEffect(() => {
        setFilteredCount(commodity_data.length);
        if (bookingFrom === 'viewBids') {
            if (activeQuotationsViewDetails) {
                console.log(activeQuotationsViewDetails, "activeQuotationsViewDetails in BookingForm");
                bookingFormik.setFieldValue('cargo_value', activeQuotationsViewDetails?.cargoValue || 0);
                bookingFormik.setFieldValue('commodity', commodity_data.find(item => Number(item.id) === Number(activeQuotationsViewDetails?.commodityId)) || null);
            }
            if (activeQuotationsViewDetails?.incotermId) {
                const incotermValue = incoterm.find(item => Number(item.value) === Number(activeQuotationsViewDetails?.incotermId));
                bookingFormik.setFieldValue('incoterms', incotermValue);

            }
        }

    }, [commodity_data, activeQuotationsViewDetails, incoterm, bookingFrom]);

    const uomLabels = {
        6: 'KG',
        7: 'MT',
    };

    // Memoize commodity data with pre-computed lowercase labels for faster filtering
    const memoizedCommodityData = useMemo(() => {
        return commodity_data.map(option => ({
            ...option,
            searchLabel: option.label.toLowerCase() // Pre-compute lowercase for faster filtering
        }));
    }, [commodity_data]);

    const commodityOptions = useCallback(
        debounce((inputValue, callback) => {
            const searchTerm = inputValue.toLowerCase();

            // Early return for empty input to show all options
            if (!searchTerm) {
                const topOptions = memoizedCommodityData
                    .slice(0, 100); // Limit to first 100 options for better performance
                callback(topOptions);
                setFilteredCount(memoizedCommodityData.length);
                return;
            }

            // Use more efficient filtering approach
            const filtered = memoizedCommodityData
                .filter(option => option.searchLabel.includes(searchTerm))
                .slice(0, 100); // Limit results for better performance

            setFilteredCount(filtered.length);
            callback(filtered);
        }, 500), // Increased debounce time for better performance
        [memoizedCommodityData]
    );

    // Add effect to clear cache when component unmounts
    useEffect(() => {
        return () => {
            clearFormCache();
        };
    }, []);
    
    return (
        <>
            <div className="air-flight_form_details">
                <Card>
                    <CardBody>
                        <form>
                            <div className="p-1">
                                {/* Route & Date */}
                                
                                <div className="d-flex justify-content-between align-items-center">
                                    <h6 className="fw-bold">
                                        <span className="text-dark">{bookingFormik?.values?.origin_port || originAddress?.code || '-'}</span> →{" "}
                                        <span className="text-dark">{bookingFormik?.values?.destination_port || destinationAddress?.code || '-'}</span>
                                    </h6>
                                    <small className="text-muted">{formatModifyDate(searchForm?.cargo_date || quoteData[0]?.validFrom)}</small>
                                </div>
                                <div className="search_result_wrap">
                                    <div className="result_tab_content_wrap">
                                        <div className="search_result_card_check_wrap d-flex align-items-center" style={{ width: '100%' }} key={`main_0`}>
                                            <div className={`search_result_card`} style={{ background: "#ffffff", border: "none", padding: '0px 0px' }}>
                                                <div className="search_result_card_header d-flex align-items-center">
                                                    <div className="card_img">
                                                        <span className='d-flex align-items-center justify-content-center img mx-auto'>
                                                            <img src={quoteData[0]?.carrierLogo ? quoteData[0]?.carrierLogo : cube_filled} alt="Logo" onError={e => { e.target.src = quoteData[0]?.carrierName?.toLowerCase() === 'oocl' ? oocl_logo : quoteData[0]?.carrierName?.toLowerCase() === 'zim' ? zim_logo : cube_filled }} />
                                                            {/*<img src={item?.carrierName?.toLowerCase() === 'oocl' ? oocl_logo : item?.carrierName?.toLowerCase() === 'zim' ? zim_logo : cube_filled} alt="Logo" />*/}
                                                        </span>
                                                        {$instantActiveTab.main === 'air_freight' || bookingType === 'air' ?
                                                            <span className="title d-block text-center mt-2">{quoteData[0]?.flightname || quoteData[0]?.carrierName || '-'}</span>
                                                            : <span className="title d-block text-center mt-2">{quoteData[0]?.carrierName || '-'}</span>}


                                                        {/* <span className="title d-block text-center mt-2"><b>Agent:</b>{quoteData[0]?.vendorName || ' -'}</span> */}
                                                    </div>
                                                    <div className="middle_content" style={{ width: '100%' }}>
                                                        {/* {console.log(instantRateLocation,"instantRateLocation")} */}
                                                        <span className="duration text-center d-block">Duration <b>{quoteData[0]?.oceanTransitTime || 0} days</b></span>
                                                        {quoteData[0]?.viaPorts?.length > 0 && (
                                                            <div className="text-center">
                                                                <span className="btn btn-link p-0 via_port_btn" id={`viaTooltip_1`}>Via Ports</span>
                                                                <UncontrolledTooltip
                                                                    placement="top"
                                                                    target={`viaTooltip_1`}
                                                                    trigger="click"
                                                                    className='bookingviaTooltip'
                                                                    autohide={true}>
                                                                    {quoteData[0]?.viaPorts?.map(t => t.code).join(', ')}
                                                                </UncontrolledTooltip>
                                                            </div>
                                                        )}
                                                        <div className="from_to_wrap multi_route mt-2 mb-3 d-flex justify-content-between">
                                                            <span className="icon d-flex align-items-center justify-content-center"><img src={ship_filled} alt="Shipping" /></span>
                                                            {/* {resultRoute?.length > 0 && resultRoute?.[index] ? resultRoute?.[index]?.map((item, index) => {
                                                            return (<span className="from_loc" key={index}>{item}</span>)
                                                        }) : null} */}
                                                            <span className="from_loc" >{searchForm?.location_from?.label || originAddress?.name || originAddress?.label || '-'}</span>
                                                            <span className="from_loc" >{searchForm?.location_to?.label || destinationAddress?.name || destinationAddress?.label || '-'}</span>
                                                        </div>
                                                        <div className="row">
                                                            <div className="col-lg-6 text-left"><span>Valid To : <b>{quoteData[0]?.validTo || new Date(searchForm?.cargo_date).toLocaleDateString() || '-'}</b></span></div>
                                                            {/* <div className="col-lg-4 text-center"><span>Id: <b>{item.id || '-'}</b></span></div> */}
                                                            {/* <div className="col-lg-6 text-end"><span>CO2: <b>{item.co_two || '-'}</b></span></div> */}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>


                                <hr />
                                <div className="row justify-content-start align-item-center">
                                    <div className="col-12 col-md-2">
                                        <div className="mb-3">
                                            <label className="form-label">Cargo Type</label>
                                            <span className="fw-400">{bookingFormik.values.cargo_type}</span>
                                        </div>
                                    </div>
                                    <div className="col-12 col-md-3">
                                        <div className="mb-3">
                                            <label className="form-label">Cargo Value</label>
                                            {/* <span className="fw-400">{bookingFormik.values.cargo_value}</span> */}
                                            <div className="input-group">
                                                <Input
                                                    type="number"
                                                    className="form-control"
                                                    placeholder="eg. 999999.99"
                                                    onChange={async(e) => {
                                                        await bookingFormik.handleChange(e, true);
                                                        bookingFormik.setFieldTouched('cargo_value', true, true);
                                                    }}
                                                    onBlur={() => bookingFormik.setFieldTouched("cargo_value", true)}
                                                    name="cargo_value"
                                                    value={bookingFormik.values.cargo_value}
                                                    invalid={bookingFormik.touched.cargo_value && bookingFormik.errors.cargo_value ? true : false}
                                                    aria-label="Amount (to the nearest dollar)"
                                                    disabled={bookingFrom === 'viewBids'}
                                                />
                                                <div className="input-group-text p-0" style={{ minWidth: '80px' }}>
                                                    <Select
                                                        className="currency-select"
                                                        classNamePrefix="select"
                                                        options={currency_data.map(currency => ({
                                                            id: currency.id,
                                                            currencyCode: currency.currencyCode,
                                                            value: currency.currencyCode,
                                                            label: `${currency.currencyCode} (${getCurrencySymbol(currency.currencyCode)})`,
                                                            version: currency.version
                                                        }))}
                                                        value={currency_data
                                                            .filter(currency => currency.currencyCode === (bookingFormik.values.quote_currency?.currencyCode || searchForm?.quote_currency?.currencyCode))
                                                            .map(currency => ({
                                                                id: currency.id,
                                                                currencyCode: currency.currencyCode,
                                                                value: currency.currencyCode,
                                                                label: `${currency.currencyCode} (${getCurrencySymbol(currency.currencyCode)})`,
                                                                version: currency.version
                                                            }))[0]}
                                                        onChange={(selectedOption) => {
                                                            bookingFormik.setFieldValue('quote_currency', selectedOption);
                                                        }}
                                                        isDisabled={bookingFrom === 'viewBids'}
                                                        isSearchable={false}
                                                        styles={{
                                                            control: (provided) => ({
                                                                ...provided,
                                                                border: 'none',
                                                                boxShadow: 'none',
                                                                height: '100%'
                                                            }),
                                                            valueContainer: (provided) => ({
                                                                ...provided,
                                                                padding: '0 8px'
                                                            })
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                            <FormFeedback>{bookingFormik.errors.cargo_value}</FormFeedback>
                                        </div>
                                    </div>
                                    <div className="col-12 col-md-4">
                                        <div className="mb-3">
                                            <label className="form-label">Commodity</label>
                                            <AsyncSelect
                                                key="commodity-select"
                                                id='commodity'
                                                name='commodity'
                                                cacheOptions
                                                defaultOptions
                                                isClearable
                                                loadOptions={commodityOptions}
                                                value={bookingFormik.values.commodity || ''}
                                                onChange={(option) => bookingFormik.setFieldValue('commodity', option)}
                                                onBlur={() => bookingFormik.setFieldTouched("commodity", true)}
                                                placeholder="Type to search commodity..."
                                                formatOptionLabel={({ label, isDisabled }, { context }) =>
                                                    context === 'menu' ? (
                                                        <div className="d-flex align-items-center">
                                                            <span style={{ opacity: isDisabled ? 0.4 : 1 }}>{label}</span>
                                                        </div>
                                                    ) : <span style={{ opacity: isDisabled ? 0.4 : 1 }}>{label}</span>
                                                }
                                                classNamePrefix="form-select"
                                                styles={{
                                                    control: (baseStyles, state) => ({
                                                        ...baseStyles,
                                                        borderColor: !bookingFormik.errors.commodity ? 'grey' : 'red',
                                                    }),
                                                    // Add performance optimizations for menu rendering
                                                    menuList: base => ({
                                                        ...base,
                                                        height: '200px', // Limit height for better performance
                                                    }),
                                                    option: (base, state) => ({
                                                        ...base,
                                                        padding: '8px 12px', // Slightly more compact options
                                                        cursor: 'pointer',
                                                    })
                                                }}
                                                // Performance optimizations
                                                filterOption={null} // Disable client-side filtering (we do it in loadOptions)
                                                isSearchable={true}
                                                backspaceRemovesValue={true}
                                                blurInputOnSelect={true}
                                                closeMenuOnSelect={true}
                                                menuShouldBlockScroll={false}
                                                menuPlacement="auto"
                                                menuPosition="fixed"
                                                maxMenuHeight={300}
                                                loadingMessage={() => "Loading commodities..."}
                                                noOptionsMessage={() => "No matching commodities found"}
                                                isDisabled={bookingFrom === 'viewBids'}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-12 col-md-3">
                                        <div className="mb-3">
                                            <label className="form-label">Incoterms</label>
                                            <div className='field_wrap'>
                                                <Select
                                                    name='incoterms'
                                                    value={bookingFormik?.values?.incoterms}
                                                    onChange={(option) => bookingFormik.setFieldValue('incoterms', option)}
                                                    onBlur={() => bookingFormik.setFieldTouched("incoterms", true)}
                                                    placeholder="Select Incoterms"
                                                    options={incoterm || []}
                                                    classNamePrefix="form-select"
                                                    menuPlacement="auto"
                                                    isDisabled={bookingFrom === 'viewBids'}
                                                    styles={{
                                                        control: (baseStyles, state) => ({
                                                            ...baseStyles,
                                                            borderColor: !bookingFormik.errors.incoterms ? 'grey' : 'red',
                                                        }),
                                                        // Add performance optimizations for menu rendering
                                                        menuList: base => ({
                                                            ...base,
                                                            height: '200px', // Limit height for better performance
                                                        }),
                                                        option: (base, state) => ({
                                                            ...base,
                                                            padding: '8px 12px', // Slightly more compact options
                                                            cursor: 'pointer',
                                                        })
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    {$instantActiveTab?.sub === 'FCL' || bookingType === 'ocean_fcl' && (
                                        <div className="row mb-3">
                                            <div className="col-md-12">
                                                <div className="border p-3">
                                                    <div className="row fw-semibold text-center mb-2">
                                                        <div className="col-md-4">Container Type</div>
                                                        <div className="col-md-4">No of Units</div>
                                                        <div className="col-md-4">Weight/Unit</div>
                                                    </div>
                                                    {activeQuotationsViewDetails?.inquiryField?.containerDetails.map((item, index) => {
                                                        return (
                                                            <div className="row mb-2" key={index}>
                                                                <div className="col-md-4">
                                                                    <input type="text" className="form-control" value={container_data.find(obj => String(obj?.id) === String(item?.id))?.label || 'N/A'} readOnly />
                                                                </div>
                                                                <div className="col-md-4">
                                                                    <input type="number" className="form-control" value={item?.noOfUnits || item?.unitNew} readOnly />
                                                                </div>
                                                                <div className="col-md-4">
                                                                    <input type="text" className="form-control" value={`${item?.weight} ${uomLabels[item?.cargoWeightUOMId || item?.uomWeight]}`} readOnly />
                                                                </div>
                                                            </div>
                                                        )
                                                    })}
                                                    {searchForm?.container_type?.containerArray?.map((item, index) => {
                                                        return (
                                                            <div className="row mb-2" key={index}>
                                                                <div className="col-md-4">
                                                                    <input type="text" className="form-control" value={container_data.find(obj => String(obj?.id) === String(item?.id))?.label || 'N/A'} readOnly />
                                                                </div>
                                                                <div className="col-md-4">
                                                                    <input type="number" className="form-control" value={item?.noOfUnits || item?.unitNew} readOnly />
                                                                </div>
                                                                <div className="col-md-4">
                                                                    <input type="text" className="form-control" value={`${item?.weight} ${uomLabels[item?.cargoWeightUOMId || item?.uomWeight]}`} readOnly />
                                                                </div>
                                                            </div>
                                                        )
                                                    })}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {$instantActiveTab?.main === 'air_freight' || bookingType === 'air' && (
                                        <div className="row justify-content-start align-item-center">
                                            <div className="col-12 col-md-3">
                                                <div className="mb-3">
                                                    <label className="form-label">ChargeAble Wt</label>
                                                    <span className="fw-400">{searchForm?.shipment_details?.chargeableWt || '-'}</span>
                                                </div>
                                            </div>
                                            <div className="col-12 col-md-3">
                                                <div className="mb-3">
                                                    <label className="form-label">Volumetric Weight</label>
                                                    <span className="fw-400">{searchForm?.shipment_details?.v_weight || activeQuotationsViewDetails?.inquiryField?.volumetricWeight}</span>
                                                </div>
                                            </div>
                                            <div className="col-12 col-md-3">
                                                <div className="mb-3">
                                                    <label className="form-label">Weight {`(${searchForm?.shipment_details?.weight_unit || 'KG'})`}</label>
                                                    <span className="fw-400">{searchForm?.shipment_details?.weight || activeQuotationsViewDetails?.inquiryField?.actualWeight || '-'}</span>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {$instantActiveTab?.sub !== 'FCL' || bookingType === 'air' && (
                                        <div className='row' style={{ marginLeft: "-19px" }}>
                                            <div className="col-12 col-md-12">
                                                <Card>
                                                    <CardBody>
                                                        {activeQuotationsViewDetails?.inquiryField?.shipmentDetails?.length > 0 && (
                                                            activeQuotationsViewDetails?.inquiryField?.shipmentDetails?.map((item, index) => (
                                                                <div className="row mb-2" key={index}>
                                                                    <div className="col-12 col-md-1">
                                                                        <label className="form-label">Unit</label>
                                                                        <span className="fw-400">{item?.noOfUnits || 0}</span>
                                                                    </div>
                                                                    <div className="col-12 col-md-2">
                                                                        <label className="form-label">Weight Per Unit</label>
                                                                        <span className="fw-400">{item?.weightPerUnit || 0} {item?.weightUOM || 'KG'}</span>
                                                                    </div>
                                                                    <div className="col-12 col-md-2">
                                                                        <div className="mb-3">
                                                                            <label className="form-label">Length</label>
                                                                            <span className="fw-400">{item?.length || 0} {item?.uom || ''}</span>
                                                                        </div>
                                                                    </div>
                                                                    <div className="col-12 col-md-2">
                                                                        <div className="mb-3">
                                                                            <label className="form-label">Width</label>
                                                                            <span className="fw-400">{item?.width || 0} {item?.uom || ''}</span>
                                                                        </div>
                                                                    </div>
                                                                    <div className="col-12 col-md-2">
                                                                        <div className="mb-3">
                                                                            <label className="form-label">Height</label>
                                                                            <span className="fw-400">{item?.height || 0} {item?.uom || ''}</span>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ))
                                                        )}
                                                        <FormikProvider value={bookingFormik}>
                                                            <FieldArray name="shipment_details">
                                                                {(arrayHelpers, i) => {
                                                                    return (
                                                                        <React.Fragment key={i}>
                                                                            {bookingFormik.values.shipment_details?.length > 0 &&
                                                                                bookingFormik.values.shipment_details.map((item, index) => (
                                                                                    <div className="row mb-2" key={index}>
                                                                                        <div className="col-12 col-md-1">
                                                                                            <label className="form-label">Unit</label>
                                                                                            <span className="fw-400">{item?.unit || 0}</span>
                                                                                        </div>
                                                                                        <div className="col-12 col-md-2">
                                                                                            <label className="form-label">Weight Per Unit</label>
                                                                                            <span className="fw-400">{item?.weightPerUnit || 0}</span>
                                                                                        </div>
                                                                                        <div className="col-12 col-md-2">
                                                                                            <div className="mb-3">
                                                                                                <label className="form-label">Length</label>
                                                                                                <span className="fw-400">{item?.length || 0} {item?.dimensions_unit || ''}</span>
                                                                                            </div>
                                                                                        </div>
                                                                                        <div className="col-12 col-md-2">
                                                                                            <div className="mb-3">
                                                                                                <label className="form-label">Width</label>
                                                                                                <span className="fw-400">{item?.width || 0} {item?.dimensions_unit || ''}</span>
                                                                                            </div>
                                                                                        </div>
                                                                                        <div className="col-12 col-md-2">
                                                                                            <div className="mb-3">
                                                                                                <label className="form-label">Height</label>
                                                                                                <span className="fw-400">{(item?.height ?? 0)}{item?.dimensions_unit ? ` ${item.dimensions_unit}` : ''}</span>
                                                                                            </div>
                                                                                        </div>
                                                                                        <div className="col-12 col-md-3">
                                                                                            <div className="mb-3">
                                                                                                <label className="form-label">Volumentric Wt</label>
                                                                                                <span className="fw-400">{item?.volumentric_wt || 0} {item?.weightUOM}</span>
                                                                                            </div>
                                                                                        </div>
                                                                                    </div>
                                                                                ))}
                                                                        </React.Fragment>
                                                                    )
                                                                }}
                                                            </FieldArray>
                                                        </FormikProvider>
                                                    </CardBody>
                                                </Card>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="d-flex justify-content-end">
                                {/* <button className="btn btn-secondary me-2" onClick={(e) => {
                                    e.preventDefault();
                                    clearFormCache();
                                }}>Clear Form</button> */}
                                <button className="btn btn-primary" onClick={(e) => {
                                    e.preventDefault();
                                    proceedToNext("billingdetails");
                                }}>Continue</button>
                            </div>
                        </form>
                    </CardBody>
                </Card>
            </div>
        </>
    )
}

export default ShipmentInformationForm;
